import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import Collapse from '@mui/material/Collapse';
import { GridColDef } from '@mui/x-data-grid';
import { PageHeader } from './layout/PageHeader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { usersApi, DbUser } from '../api/client';
import { capacityPlansApi, CapacityPlan, resourceAssignmentsApi, ResourceAssignment, ppmProjectsApi, PpmProjectListItem } from '../api/ppm';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage } from '../utils/rbac';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens } from '../theme/tokens';
import { formatPercent } from '../utils/finance';
import Autocomplete from '@mui/material/Autocomplete';
import { PageLoader } from './PageLoader';
import { ResourceSchedule, ScheduleAssignment } from './capacity/ResourceSchedule';

const MS_DAY = 86400000;

function yyyyMm(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthOptions(count = 12) {
  const base = new Date();
  base.setDate(1);
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(base);
    d.setMonth(base.getMonth() - i);
    out.push(yyyyMm(d));
  }
  return out;
}

const CapacityPlanningPage: React.FC = () => {
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'capacity');

  const [period, setPeriod] = useState<string>(yyyyMm(new Date()));
  const [department, setDepartment] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [users, setUsers] = useState<DbUser[]>([]);
  const [capacityPlans, setCapacityPlans] = useState<CapacityPlan[]>([]);
  const [assignments, setAssignments] = useState<ResourceAssignment[]>([]);
  const [internalProjects, setInternalProjects] = useState<PpmProjectListItem[]>([]);

  const [creating, setCreating] = useState(false);
  const [newAssignment, setNewAssignment] = useState<{
    userId: string;
    projectId: string;
    role: string;
    hoursAllocated: string;
    startDate: string;
    endDate: string;
  }>({ userId: '', projectId: '', role: 'Contributor', hoursAllocated: '40', startDate: new Date().toISOString().slice(0, 10), endDate: '' });

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<CapacityPlan | null>(null);
  const [savingPlan, setSavingPlan] = useState(false);

  const [topPanel, setTopPanel] = useState<null | 'capacity' | 'allocated' | 'utilization' | 'overallocated'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);
  const [planForm, setPlanForm] = useState<{
    userId: string;
    department: string;
    month: string; // YYYY-MM
    totalCapacity: string;
    allocated: string;
    available: string;
    approved: string;
  }>({ userId: '', department: '', month: yyyyMm(new Date()), totalCapacity: '160', allocated: '0', available: '160', approved: '0' });

  const userById = useMemo(() => {
    const m = new Map<string, DbUser>();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const [uRes, cpRes, aRes, pRes] = await Promise.all([
        usersApi.getAll({ scopePage: 'capacity' }).catch(() => ({ data: [] })),
        capacityPlansApi.getAll({ period }).catch(() => ({ data: [] })),
        resourceAssignmentsApi.getAll({ active: true }).catch(() => ({ data: [] })),
        ppmProjectsApi.getAll({ source: 'internal' }).catch(() => ({ data: [] })),
      ]);
      setUsers((uRes.data || []) as any);
      setCapacityPlans((cpRes.data || []) as any);
      setAssignments((aRes.data || []) as any);
      setInternalProjects((pRes.data || []) as any);
      setNewAssignment((v) => ({
        ...v,
        userId: v.userId || (uRes.data?.[0]?.id || ''),
        projectId: v.projectId || (pRes.data?.[0]?.id || ''),
      }));
      setPlanForm((v) => ({
        ...v,
        userId: v.userId || (uRes.data?.[0]?.id || ''),
        department: v.department || (uRes.data?.[0]?.department || ''),
      }));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load capacity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const doRefresh = async () => {
      try {
        setLoading(true);
        setError(null);
        const [uRes, cpRes, aRes, pRes] = await Promise.all([
          usersApi.getAll({ scopePage: 'capacity' }).catch(() => ({ data: [] })),
          capacityPlansApi.getAll({ period }).catch(() => ({ data: [] })),
          resourceAssignmentsApi.getAll({ active: true }).catch(() => ({ data: [] })),
          ppmProjectsApi.getAll({ source: 'internal' }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setUsers((uRes.data || []) as any);
        setCapacityPlans((cpRes.data || []) as any);
        setAssignments((aRes.data || []) as any);
        setInternalProjects((pRes.data || []) as any);
        setNewAssignment((v) => ({
          ...v,
          userId: v.userId || (uRes.data?.[0]?.id || ''),
          projectId: v.projectId || (pRes.data?.[0]?.id || ''),
        }));
        setPlanForm((v) => ({
          ...v,
          userId: v.userId || (uRes.data?.[0]?.id || ''),
          department: v.department || (uRes.data?.[0]?.department || ''),
        }));
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load capacity data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    doRefresh();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const departmentOptions = useMemo(() => {
    const set = new Set<string>();
    (users || []).forEach((u: any) => {
      const d = String(u?.department || '').trim();
      if (d) set.add(d);
    });
    (capacityPlans || []).forEach((p: any) => {
      const d = String(p?.department || '').trim();
      if (d) set.add(d);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [users, capacityPlans]);

  const plansForView = useMemo(() => {
    const q = String(department || '').trim().toLowerCase();
    if (!q) return capacityPlans;
    return (capacityPlans || []).filter((p: any) => String(p?.department || '').toLowerCase().includes(q));
  }, [capacityPlans, department]);

  // --- Schedule (Smartsheet-style timeline) data ---
  const scheduleAssignments = useMemo<ScheduleAssignment[]>(() => {
    return (assignments || [])
      .map((a: any) => {
        const startRaw = a?.startDate ? new Date(a.startDate) : null;
        if (!startRaw || isNaN(startRaw.getTime())) return null;
        const endRaw = a?.endDate ? new Date(a.endDate) : null;
        const end = endRaw && !isNaN(endRaw.getTime()) ? endRaw : new Date(startRaw.getTime() + 28 * MS_DAY);
        const project = a?.Project?.name || internalProjects.find((p) => p.id === String(a?.projectId))?.name || 'Project';
        return {
          id: String(a.id),
          userId: String(a?.userId || ''),
          label: project,
          role: a?.role || undefined,
          hoursAllocated: Number(a?.hoursAllocated || 0),
          start: startRaw,
          end,
        } as ScheduleAssignment;
      })
      .filter(Boolean) as ScheduleAssignment[];
  }, [assignments, internalProjects]);

  const weeklyCapacityMap = useMemo(() => {
    const m: Record<string, number> = {};
    (capacityPlans || []).forEach((p: any) => {
      if (p?.userId) {
        const wk = Number(p.totalCapacity || 0) / 4.345; // monthly hours → weekly
        if (wk > 0) m[String(p.userId)] = wk;
      }
    });
    return m;
  }, [capacityPlans]);

  const scheduleUsers = useMemo(() => {
    const withAssign = new Set(scheduleAssignments.map((a) => a.userId));
    const q = String(department || '').trim().toLowerCase();
    return (users || [])
      .filter((u: any) => withAssign.has(String(u.id)))
      .filter((u: any) => !q || String(u?.department || '').toLowerCase().includes(q))
      .map((u: any) => ({
        id: String(u.id),
        name: u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'User',
        subtitle: u.jobTitle || u.department || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, scheduleAssignments, department]);

  const totals = useMemo(() => {
    const totalCapacity = plansForView.reduce((s, r) => s + (Number(r.totalCapacity) || 0), 0);
    const allocated = plansForView.reduce((s, r) => s + (Number(r.allocated) || 0), 0);
    const available = plansForView.reduce((s, r) => s + (Number(r.available) || 0), 0);
    const util = totalCapacity > 0 ? (allocated / totalCapacity) * 100 : 0;
    const overallocated = plansForView.filter((r) => (Number(r.totalCapacity) || 0) > 0 && (Number(r.allocated) || 0) > (Number(r.totalCapacity) || 0)).length;
    return { totalCapacity, allocated, available, util, overallocated };
  }, [plansForView]);

  const planPanelRows = useMemo(() => {
    return (plansForView || []).map((r: any) => {
      const cap = Number(r.totalCapacity || 0);
      const alloc = Number(r.allocated || 0);
      const utilPct = cap > 0 ? (alloc / cap) * 100 : 0;
      const u = r.userId ? userById.get(String(r.userId)) : undefined;
      const userDisplay = u?.displayName || u?.email || String(r.userId || '—');
      return {
        ...r,
        totalCapacityNum: cap,
        allocatedNum: alloc,
        utilPct,
        userDisplay,
      };
    });
  }, [plansForView, userById]);

  const panelPlans = useMemo(() => {
    if (!topPanel) return [];
    const base = planPanelRows.slice();
    if (topPanel === 'capacity') return base.sort((a, b) => (b.totalCapacityNum || 0) - (a.totalCapacityNum || 0));
    if (topPanel === 'allocated') return base.sort((a, b) => (b.allocatedNum || 0) - (a.allocatedNum || 0));
    if (topPanel === 'utilization') return base.sort((a, b) => (b.utilPct || 0) - (a.utilPct || 0));
    // overallocated
    return base.filter((r) => (r.totalCapacityNum || 0) > 0 && (r.allocatedNum || 0) > (r.totalCapacityNum || 0)).sort((a, b) => (b.utilPct || 0) - (a.utilPct || 0));
  }, [planPanelRows, topPanel]);

  const panelCols: GridColDef[] = useMemo(() => {
    return [
      { field: 'userDisplay', headerName: 'User', flex: 1, minWidth: 220 },
      { field: 'department', headerName: 'Dept', width: 160, valueGetter: (p) => p.row?.department || '—' },
      { field: 'totalCapacityNum', headerName: 'Capacity', width: 120, valueGetter: (p) => Number(p.row?.totalCapacityNum || 0) },
      { field: 'allocatedNum', headerName: 'Allocated', width: 120, valueGetter: (p) => Number(p.row?.allocatedNum || 0) },
      {
        field: 'utilPct',
        headerName: 'Util %',
        width: 110,
        valueGetter: (p) => `${Math.round(Number(p.row?.utilPct || 0))}%`,
      },
    ];
  }, []);

  const utilBuckets = useMemo(() => {
    const buckets = [
      { name: '<50%', value: 0 },
      { name: '50–80%', value: 0 },
      { name: '80–100%', value: 0 },
      { name: '100–120%', value: 0 },
      { name: '120%+', value: 0 },
    ];
    planPanelRows.forEach((r: any) => {
      const pct = Number(r.utilPct || 0);
      if (pct < 50) buckets[0].value += 1;
      else if (pct < 80) buckets[1].value += 1;
      else if (pct <= 100) buckets[2].value += 1;
      else if (pct <= 120) buckets[3].value += 1;
      else buckets[4].value += 1;
    });
    return buckets;
  }, [planPanelRows]);

  const overallocTop = useMemo(() => {
    return planPanelRows
      .filter((r: any) => (r.totalCapacityNum || 0) > 0 && (r.allocatedNum || 0) > (r.totalCapacityNum || 0))
      .slice()
      .sort((a: any, b: any) => (b.utilPct || 0) - (a.utilPct || 0))
      .slice(0, 8)
      .map((r: any) => ({ name: r.userDisplay, value: Math.round(Number(r.utilPct || 0)) }));
  }, [planPanelRows]);

  const totalMix = useMemo(() => {
    const cap = Number(totals.totalCapacity || 0);
    const alloc = Number(totals.allocated || 0);
    return [
      { name: 'Allocated', value: alloc },
      { name: 'Available', value: Math.max(0, cap - alloc) },
    ];
  }, [totals.totalCapacity, totals.allocated]);

  const removeAssignment = async (id: string) => {
    try {
      await resourceAssignmentsApi.remove(id);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete assignment');
    }
  };

  const openNewPlan = () => {
    setEditingPlan(null);
    const u = users[0];
    setPlanForm({
      userId: u?.id || '',
      department: u?.department || '',
      month: period,
      totalCapacity: '160',
      allocated: '0',
      available: '160',
      approved: '0',
    });
    setPlanDialogOpen(true);
  };

  const openEditPlan = (row: CapacityPlan) => {
    setEditingPlan(row);
    setPlanForm({
      userId: row.userId || '',
      department: row.department || '',
      month: String(row.month).slice(0, 7),
      totalCapacity: String(row.totalCapacity ?? 0),
      allocated: String(row.allocated ?? 0),
      available: String(row.available ?? 0),
      approved: String(row.approved ?? 0),
    });
    setPlanDialogOpen(true);
  };

  const savePlan = async () => {
    try {
      setSavingPlan(true);
      const monthDate = new Date(`${planForm.month}-01T00:00:00.000Z`);
      const payload = {
        userId: planForm.userId || null,
        department: planForm.department || null,
        month: monthDate,
        totalCapacity: Number(planForm.totalCapacity) || 0,
        allocated: Number(planForm.allocated) || 0,
        available: Number(planForm.available) || 0,
        approved: Number(planForm.approved) || 0,
        metadata: { source: 'ui' },
      };

      if (editingPlan?.id) {
        await capacityPlansApi.update(editingPlan.id, payload);
      } else {
        await capacityPlansApi.create(payload);
      }
      setPlanDialogOpen(false);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save capacity plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const deletePlan = async (id: string) => {
    try {
      await capacityPlansApi.remove(id);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete capacity plan');
    }
  };

  const createAssignment = async () => {
    if (!newAssignment.userId || !newAssignment.projectId) return;
    try {
      setCreating(true);
      await resourceAssignmentsApi.create({
        userId: newAssignment.userId,
        projectId: newAssignment.projectId,
        role: newAssignment.role || undefined,
        hoursAllocated: Number(newAssignment.hoursAllocated) || 0,
        startDate: new Date((newAssignment.startDate || new Date().toISOString().slice(0, 10)) + 'T00:00:00.000Z').toISOString(),
        endDate: newAssignment.endDate ? new Date(newAssignment.endDate).toISOString() : null,
      });
      setNewAssignment((v) => ({ ...v, hoursAllocated: '40', endDate: '' }));
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to create assignment');
    } finally {
      setCreating(false);
    }
  };

  const capacityCols: GridColDef[] = [
    {
      field: 'userId',
      headerName: 'User',
      flex: 1.1,
      minWidth: 200,
      valueGetter: (params) => {
        const u = userById.get(String(params.value));
        return u?.displayName || u?.email || String(params.value);
      },
    },
    {
      field: 'department',
      headerName: 'Department',
      width: 120,
      valueGetter: (params) => (params.row as any)?.department || '—',
    },
    { field: 'totalCapacity', headerName: 'Capacity', width: 95, type: 'number' },
    { field: 'allocated', headerName: 'Allocated', width: 95, type: 'number' },
    { field: 'available', headerName: 'Available', width: 95, type: 'number' },
    {
      field: 'utilization',
      headerName: 'Utilization',
      minWidth: 220,
      flex: 1,
      sortable: false,
      valueGetter: (params) => {
        const r: any = params.row;
        const cap = Number(r?.totalCapacity) || 0;
        const alloc = Number(r?.allocated) || 0;
        return cap > 0 ? (alloc / cap) * 100 : 0;
      },
      renderCell: (params) => {
        const v = Math.max(0, Math.min(140, Number(params.value) || 0));
        const color = v >= 100 ? 'error' : v >= 90 ? 'warning' : 'primary';
        return (
          <Box sx={{ width: '100%', pr: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Box component="span" sx={{ fontSize: 12, fontWeight: 650, color: tokens.color.text }}>
                {Math.round(Number(params.value) || 0)}%
              </Box>
              <Chip
                size="small"
                label={v >= 100 ? 'Over' : v >= 90 ? 'Full' : 'Room'}
                color={color as any}
                variant="outlined"
                sx={{ height: 20, fontWeight: 650 }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, v)}
              color={color as any}
              sx={{ height: 8, borderRadius: 999, backgroundColor: tokens.color.neutralSoft }}
            />
          </Box>
        );
      },
    },
    ...(canEdit
      ? ([
          {
            field: 'actions',
            headerName: '',
            width: 72,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <Stack direction="row" spacing={0.25}>
                <Tooltip title="Edit capacity plan">
                  <IconButton size="small" onClick={() => openEditPlan(params.row as any)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete capacity plan">
                  <IconButton size="small" onClick={() => deletePlan(String((params.row as any).id))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
        ] as GridColDef[])
      : []),
  ];

  const assignmentCols: GridColDef[] = [
    {
      field: 'userId',
      headerName: 'User',
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => {
        const row: any = params.row;
        const v = params.value;
        return row?.User?.displayName || row?.User?.email || userById.get(String(v))?.displayName || String(v);
      },
    },
    {
      field: 'projectId',
      headerName: 'Project',
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => {
        const row: any = params.row;
        const v = params.value;
        return row?.Project?.name || internalProjects.find((p) => p.id === String(v))?.name || String(v);
      },
    },
    { field: 'role', headerName: 'Role', width: 140, valueGetter: (params) => (params.row as any)?.role || '—' },
    { field: 'hoursAllocated', headerName: 'Hours', width: 110, type: 'number', valueGetter: (params) => (params.row as any)?.hoursAllocated ?? '—' },
    { field: 'startDate', headerName: 'Start', width: 120, valueGetter: (params) => ((params.row as any)?.startDate ? String((params.row as any).startDate).slice(0, 10) : '—') },
    { field: 'endDate', headerName: 'End', width: 120, valueGetter: (params) => ((params.row as any)?.endDate ? String((params.row as any).endDate).slice(0, 10) : '—') },
    ...(canEdit
      ? ([
          {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <Tooltip title="Delete assignment">
                <IconButton size="small" onClick={() => removeAssignment(String(params.row.id))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          },
        ] as GridColDef[])
      : []),
  ];

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
        <PageHeader
        title="Capacity Planning"
        subtitle="Demand vs capacity, utilization, and assignment management."
        chips={[
          { label: `Period: ${period}`, color: 'default' },
          ...(department ? [{ label: `Dept: ${department}`, color: 'default' as const }] : []),
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {monthOptions(12).map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              freeSolo
              options={departmentOptions}
              value={department || ''}
              onChange={(_, v) => setDepartment(String(v || ''))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Department"
                  placeholder="Search or type a new department…"
                  sx={{ minWidth: 240 }}
                />
              )}
            />
            <Button size="small" variant="outlined" onClick={refresh} sx={{ borderRadius: 2 }}>
              Refresh
            </Button>
            {canEdit ? (
              <Button
                size="small"
                variant="contained"
                onClick={openNewPlan}
                sx={{ borderRadius: 2 }}
              >
                New capacity plan
              </Button>
            ) : null}
          </Box>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Capacity"
            value={totals.totalCapacity}
            subtitle="Total hours"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'capacity' ? null : 'capacity'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Allocated"
            value={totals.allocated}
            subtitle="Assigned hours"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'allocated' ? null : 'allocated'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Utilization"
            value={`${Math.round(totals.util)}%`}
            subtitle="Allocated / capacity"
            accent={tokens.color.success}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'utilization' ? null : 'utilization'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Overallocated"
            value={totals.overallocated}
            subtitle="> 100% utilized"
            accent={tokens.color.danger}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'overallocated' ? null : 'overallocated'));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={
                topPanel === 'capacity'
                  ? 'Capacity (highest)'
                  : topPanel === 'allocated'
                    ? 'Allocated (highest)'
                    : topPanel === 'utilization'
                      ? 'Utilization (highest)'
                      : 'Overallocated plans'
              }
              subtitle="Click any row to edit the plan (admin)."
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                    Close
                  </Button>
                </Stack>
              }
            >
              {!panelPlans.length ? (
                <Alert severity="info" variant="outlined">
                  No results.
                </Alert>
              ) : showAllPanel ? (
                <div style={{ width: '100%', height: 460 }}>
                  <ResizableDataGrid
                    storageKey={`gridWidths.capacity.topPanel.${topPanel}`}
                    enableDensityControls
                    rows={panelPlans}
                    columns={panelCols}
                    getRowId={(r) => r.id}
                    disableRowSelectionOnClick
                    onRowClick={(p) => (canEdit ? openEditPlan(p.row as any) : undefined)}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: tokens.color.surfaceMuted },
                      '& .MuiDataGrid-row:hover': { backgroundColor: tokens.color.surfaceMuted },
                    }}
                  />
                </div>
              ) : (
                <Stack spacing={1}>
                  {panelPlans.slice(0, 12).map((r: any) => (
                    <Box
                      key={r.id}
                      sx={{ p: 1, borderRadius: 2, border: `1px solid ${tokens.color.border}`, cursor: canEdit ? 'pointer' : 'default', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                      onClick={() => (canEdit ? openEditPlan(r) : undefined)}
                    >
                      <Typography sx={{ fontWeight: 650 }} noWrap>
                        {r.userDisplay}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        cap {Math.round(Number(r.totalCapacityNum || 0))} • alloc {Math.round(Number(r.allocatedNum || 0))} • {Math.round(Number(r.utilPct || 0))}%
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </TableCard>
          </Collapse>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <TableCard
          title="Schedule"
          subtitle="Allocation by team member over time. Bars turn red when someone is over-allocated (> 100%)."
        >
          <ResourceSchedule users={scheduleUsers} assignments={scheduleAssignments} weeklyCapacity={weeklyCapacityMap} />
        </TableCard>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard title="Utilization distribution" subtitle="Per-user plan utilization" height={260}>
            <NexusBarChart
              data={utilBuckets}
              xKey="name"
              height={232}
              series={[{ key: 'value', name: 'Users', color: tokens.color.accent }]}
              valueFormatter={(v) => `${v}`}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Allocation mix" subtitle="Allocated vs available hours" height={260}>
            <NexusDonut
              height={200}
              centerLabel="Util"
              centerValue={`${Math.round(totals.util)}%`}
              data={[
                { name: 'Allocated', value: totalMix[0]?.value || 0, color: tokens.color.accent },
                { name: 'Available', value: totalMix[1]?.value || 0, color: tokens.color.neutralSoft },
              ]}
              valueFormatter={(v) => `${Math.round(v)}h`}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Top overallocated" subtitle="Highest utilization %" height={260}>
            {overallocTop.length ? (
              <NexusBarChart
                data={overallocTop}
                xKey="name"
                height={232}
                series={[{ key: 'value', name: 'Utilization', color: tokens.color.danger }]}
                valueFormatter={(v) => formatPercent(v)}
              />
            ) : (
              <Box sx={{ height: 232, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.color.textMuted, fontSize: 13 }}>
                No overallocated plans.
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableCard title="Capacity plans" subtitle="Per-user monthly capacity and utilization.">
              <div style={{ width: '100%', height: 520 }}>
                <ResizableDataGrid
                  storageKey="gridWidths.capacity.plans"
                  enableDensityControls
                  rows={capacityPlans}
                  columns={capacityCols}
                  getRowId={(r) => r.id}
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                />
              </div>
            </TableCard>
          </Grid>

          <Grid item xs={12}>
            <TableCard
              title="Assignments"
              subtitle="Active allocations across projects."
              actions={
                canEdit ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                      select
                      size="small"
                      label="User"
                      value={newAssignment.userId}
                      onChange={(e) => setNewAssignment((v) => ({ ...v, userId: e.target.value }))}
                      sx={{ minWidth: 220 }}
                    >
                      {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.displayName || u.email}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Project"
                      value={newAssignment.projectId}
                      onChange={(e) => setNewAssignment((v) => ({ ...v, projectId: e.target.value }))}
                      sx={{ minWidth: 220 }}
                    >
                      {internalProjects.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Role"
                      value={newAssignment.role}
                      onChange={(e) => setNewAssignment((v) => ({ ...v, role: e.target.value }))}
                      sx={{ minWidth: 160 }}
                    />
                    <TextField
                      size="small"
                      label="Hours"
                      value={newAssignment.hoursAllocated}
                      onChange={(e) => setNewAssignment((v) => ({ ...v, hoursAllocated: e.target.value }))}
                      sx={{ width: 110 }}
                    />
                    <TextField
                      size="small"
                      label="Start"
                      type="date"
                      value={newAssignment.startDate}
                      onChange={(e) => setNewAssignment((v) => ({ ...v, startDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 160 }}
                    />
                    <TextField
                      size="small"
                      label="End"
                      type="date"
                      value={newAssignment.endDate}
                      onChange={(e) => setNewAssignment((v) => ({ ...v, endDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 160 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={createAssignment}
                      disabled={creating || !newAssignment.userId || !newAssignment.projectId}
                      sx={{ borderRadius: 2 }}
                    >
                      {creating ? 'Adding…' : 'Add assignment'}
                    </Button>
                  </Box>
                ) : null
              }
            >
              <div style={{ width: '100%', height: 520 }}>
                <ResizableDataGrid
                  storageKey="gridWidths.capacity.assignments"
                  enableDensityControls
                  rows={assignments}
                  columns={assignmentCols}
                  getRowId={(r) => r.id}
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                />
              </div>
            </TableCard>
          </Grid>
        </Grid>

        <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editingPlan ? 'Edit capacity plan' : 'New capacity plan'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField
                select
                label="User"
                value={planForm.userId}
                onChange={(e) => {
                  const u = users.find((x) => x.id === e.target.value);
                  setPlanForm((v) => ({ ...v, userId: e.target.value, department: u?.department || v.department }));
                }}
                fullWidth
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.displayName || u.email}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Month (YYYY-MM)" value={planForm.month} onChange={(e) => setPlanForm((v) => ({ ...v, month: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={departmentOptions}
                value={planForm.department || ''}
                onChange={(_, v) => setPlanForm((cur) => ({ ...cur, department: String(v || '') }))}
                renderInput={(params) => <TextField {...params} label="Department" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Total capacity" value={planForm.totalCapacity} onChange={(e) => setPlanForm((v) => ({ ...v, totalCapacity: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Allocated" value={planForm.allocated} onChange={(e) => setPlanForm((v) => ({ ...v, allocated: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Available" value={planForm.available} onChange={(e) => setPlanForm((v) => ({ ...v, available: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Approved" value={planForm.approved} onChange={(e) => setPlanForm((v) => ({ ...v, approved: e.target.value }))} fullWidth />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPlanDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={savePlan} variant="contained" disabled={savingPlan} sx={{ borderRadius: 2 }}>
            {savingPlan ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </PageLoader>
  );
};

export default CapacityPlanningPage;


